import React from 'react';
import { LEAVE_STATUSES } from '../../../../constants/leaveConstants';
import { LeaveType, LeaveStatus } from '../../../../types/leaves.types';
import { DepartmentDto } from '../../../../types/department.types';
import { Store } from '../../../../types/shop.types';
import dayjs from 'dayjs';
import FilterDropdown from '../../../shared/components/filter/FilterDropdown';
import { useTranslation } from 'react-i18next';

interface LeaveFiltersProps {
  selectedType: LeaveType | 'all';
  selectedStatus: LeaveStatus | 'all';
  selectedDateRange: [string, string] | null;
  selectedDepartment: string;
  selectedShop: string;
  departments: DepartmentDto[];
  shops: Store[];
  onTypeChange: (type: LeaveType | 'all') => void;
  onStatusChange: (status: LeaveStatus | 'all') => void;
  onDateRangeChange: (dates: [string, string] | null) => void;
  onDepartmentChange: (departmentId: string) => void;
  onShopChange: (shopId: string) => void;
  onReset: () => void;
}

const LeaveFilters: React.FC<LeaveFiltersProps> = ({
  selectedType,
  selectedStatus,
  selectedDateRange,
  selectedDepartment,
  selectedShop,
  departments,
  shops,
  onTypeChange,
  onStatusChange,
  onDateRangeChange,
  onDepartmentChange,
  onShopChange,
  onReset,
}) => {
  const { t } = useTranslation();
  
  // İzin türleri için i18n tabanlı seçenekler
  const getLeaveTypeOptions = () => {
    return [
      { value: 'all', label: t('leaves.filters.all') },
      ...Object.entries(LeaveType).map(([value, label]) => ({
        value:label,
        label: t(`common.leaveType.${label}`)
      }))
    ];
  };
  
  // İzin durumları için i18n tabanlı seçenekler
  const getLeaveStatusOptions = () => {
    return [
      { value: 'all', label: t('leaves.filters.all') },
      ...Object.keys(LEAVE_STATUSES).map((value) => ({
        value,
        label: t(`leaves.statusOptions.${value}`)
      }))
    ];
  };
  
  const filterGroups = [
    {
      label: t('leaves.filters.type'),
      type: 'select' as const,
      value: selectedType,
      onChange: onTypeChange,
      options: getLeaveTypeOptions()
    },
    {
      label: t('leaves.filters.status'),
      type: 'select' as const,
      value: selectedStatus,
      onChange: onStatusChange,
      options: getLeaveStatusOptions()
    },
    {
      label: t('leaves.filters.dateRange'),
      type: 'dateRange' as const,
      value: selectedDateRange ? [dayjs(selectedDateRange[0]), dayjs(selectedDateRange[1])] : null,
      onChange: (dates: any) => {
        if (dates) {
          onDateRangeChange([dates[0].toISOString(), dates[1].toISOString()]);
        } else {
          onDateRangeChange(null);
        }
      }
    },
    {
      label: t('leaves.filters.department'),
      type: 'select' as const,
      value: selectedDepartment,
      onChange: onDepartmentChange,
      options: [
        { value: 'all', label: t('leaves.filters.all') },
        ...departments?.map(dept => ({
          value: dept.id.toString(),
          label: dept.name
        }))
      ]
    },
    {
      label: t('leaves.filters.shop'),
      type: 'select' as const,
      value: selectedShop,
      onChange: onShopChange,
      options: [
        { value: 'all', label: t('leaves.filters.all') },
        ...shops?.map(shop => ({
          value: shop.id.toString(),
          label: shop.name
        }))
      ]
    },
  ];

  return (
    <FilterDropdown
      groups={filterGroups}
      onReset={onReset}
      buttonType="primary"
    />
  );
};

export default LeaveFilters;